import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { MesReferencia } from '../../models/contador/contador.model';
import { ContratoInfoVM } from '../../models/contador/contrato-info.model';
import { Moeda, MOEDA_CONFIG, MoedaConfig } from '../../enums/moeda.enum';

/**
 * Card APRESENTACIONAL ("burro") com os dados do contrato ativo: nao busca dados
 * nem executa acoes — recebe um `ContratoInfoVM` e emite eventos.
 *
 * "Mes de referencia" e editavel (dropdown, visao do CONTADOR) ou somente leitura
 * (texto, visao do CLIENTE) conforme `mesReferenciaEditavel`; o pai mantem o
 * valor e a logica de salvar/reverter. Reusa as chaves de i18n do Operar Cliente
 * (`contador_operar.despesa_acessoria.contrato.*`).
 */
@Component({
  selector: 'app-contrato-info-card',
  templateUrl: './contrato-info-card.component.html',
  styleUrls: ['./contrato-info-card.component.scss'],
})
export class ContratoInfoCardComponent implements OnChanges {
  /** Dados do contrato a exibir (view-model comum). */
  @Input() contrato: ContratoInfoVM | null = null;

  /** Config de formatacao da moeda do contrato (simbolo/casas/locale). */
  @Input() moedaConfig: MoedaConfig = MOEDA_CONFIG[Moeda.GUARANI];

  /** Chave i18n do titulo do card. */
  @Input() tituloKey = 'contador_operar.despesa_acessoria.contrato.titulo';

  /** Chave i18n do rotulo do campo "nome" (topo do grid). */
  @Input() nomeLabelKey = 'contador_operar.despesa_acessoria.contrato.cliente';

  /**
   * `true` => renderiza SOMENTE o grid (sem a moldura do cartao: borda/header/
   * padding), para encaixar dentro de outro card (ex.: secao "Meu contrato" do
   * cliente). Default `false` (cartao completo, como no Operar Cliente).
   */
  @Input() plano = false;

  /**
   * Exibe (ou nao) o campo "Autonomia" no grid. No contexto do cliente a
   * autonomia e um switch editavel separado, entao o campo do card e ocultado.
   */
  @Input() mostrarAutonomia = true;

  /**
   * `true` => "Mes de referencia" e um dropdown editavel (visao do contador);
   * `false` => texto somente leitura (visao do cliente).
   */
  @Input() mesReferenciaEditavel = false;

  /** Opcoes do dropdown "Mes de referencia" (rotulos ja traduzidos pelo pai). */
  @Input() mesReferenciaOptions: { label: string; value: MesReferencia }[] = [];

  /** Valor atual do "Mes de referencia" (two-way com o pai). */
  @Input() mesReferenciaValor: MesReferencia | null = null;
  @Output() mesReferenciaValorChange = new EventEmitter<MesReferencia | null>();

  /** Desabilita o dropdown enquanto o pai salva o PATCH. */
  @Input() mesReferenciaSalvando = false;

  /** Emitido quando o usuario troca o "Mes de referencia" (o pai confirma/salva). */
  @Output() mesReferenciaChange = new EventEmitter<MesReferencia>();

  /**
   * Exibe o botao "Cancelar contrato" no header (visao do contador). O card apenas
   * emite `cancelar`; o pai abre o dialog de confirmacao com a data e chama a API.
   */
  @Input() mostrarCancelar = false;

  /** Emitido ao clicar em "Cancelar contrato" (o pai confirma e cancela). */
  @Output() cancelar = new EventEmitter<void>();

  /**
   * Edicao inline do VALOR MENSAL (visao do contador): troca o texto por um
   * inputNumber. Emite `valorMensalChange` ao confirmar (blur/Enter) quando o
   * valor muda; o pai chama a API e propaga as parcelas/titulos em aberto.
   */
  @Input() valorMensalEditavel = false;
  @Input() valorMensalSalvando = false;
  @Output() valorMensalChange = new EventEmitter<number>();

  /** Edicao inline do DIA DE COBRANCA (mesmo padrao do valor mensal). */
  @Input() diaCobrancaEditavel = false;
  @Input() diaCobrancaSalvando = false;
  @Output() diaCobrancaChange = new EventEmitter<number>();

  /** Edicao inline das PARCELAS POR MES (mesmo padrao do dia; NAO recalcula existentes). */
  @Input() parcelasPorMesEditavel = false;
  @Input() parcelasPorMesSalvando = false;
  @Output() parcelasPorMesChange = new EventEmitter<number>();

  /** Estado local dos inputs inline (espelha o contrato; editavel). */
  valorMensalEdit: number | null = null;
  /** String formatada do valor mensal exibida no input (ex.: "200.000"). */
  valorMensalStr = '';
  diaCobrancaEdit: number | null = null;
  parcelasPorMesEdit: number | null = null;

  /** Casas decimais da moeda do contrato (PYG = 0; demais = 2). */
  private get casasDecimaisMoeda(): number {
    return this.moedaConfig.digits === '1.0-0' ? 0 : 2;
  }

  /** Símbolo curto da moeda exibido dentro do input (ex.: "Gs"). */
  get moedaSimbolo(): string {
    switch (this.moedaConfig.symbol) {
      case 'BRL':
        return 'R$';
      case 'USD':
        return '$';
      case 'ARS':
        return '$';
      case 'PYG':
        return 'Gs';
      default:
        return this.moedaConfig.symbol;
    }
  }

  /** Sincroniza os inputs inline sempre que o contrato (ou moeda) muda. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contrato'] || changes['moedaConfig']) {
      this.valorMensalEdit = this.contrato?.valorMensal ?? null;
      this.valorMensalStr = this.formatarValorParaExibicao(this.valorMensalEdit);
      this.diaCobrancaEdit = this.contrato?.diaCobranca ?? null;
      this.parcelasPorMesEdit = this.contrato?.parcelasPorMes ?? null;
    }
  }

  /**
   * Formata o valor mensal enquanto o usuário digita (mesmo padrão do input de
   * preço do formulário de item): mantém só dígitos, aplica separador de milhar da
   * moeda e atualiza o número editado (`valorMensalEdit`).
   */
  formatarValorMensal(event: Event): void {
    const input = event.target as HTMLInputElement;
    const onlyDigits = (input.value || '').replace(/\D+/g, '');
    if (!onlyDigits) {
      this.valorMensalEdit = null;
      this.valorMensalStr = '';
      input.value = '';
      return;
    }
    const { valor, formatted } = this.calcularValorFormatado(onlyDigits, this.casasDecimaisMoeda);
    this.valorMensalEdit = valor;
    this.valorMensalStr = formatted;
    input.value = formatted;
  }

  /** Confirma o valor mensal (Enter ou botão): emite só quando muda e é válido. */
  confirmarValorMensal(): void {
    const atual = this.contrato?.valorMensal ?? null;
    const novo = this.valorMensalEdit;
    if (novo != null && novo > 0 && novo !== atual) {
      this.valorMensalChange.emit(novo);
    } else {
      this.reverterValorMensal();
    }
  }

  /** Reverte o input de valor ao valor atual do contrato (ex.: ao sair sem confirmar). */
  reverterValorMensal(): void {
    const atual = this.contrato?.valorMensal ?? null;
    this.valorMensalEdit = atual;
    this.valorMensalStr = this.formatarValorParaExibicao(atual);
  }

  /** Número inteiro/decimal + string formatada a partir de dígitos puros. */
  private calcularValorFormatado(
    onlyDigits: string,
    casasDecimais: number,
  ): { valor: number | null; formatted: string } {
    if (casasDecimais === 0) {
      const intVal = parseInt(onlyDigits, 10);
      if (isNaN(intVal)) return { valor: null, formatted: '' };
      return {
        valor: intVal,
        formatted: intVal.toLocaleString(this.moedaConfig.locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }),
      };
    }
    const cents = parseInt(onlyDigits, 10);
    if (isNaN(cents)) return { valor: null, formatted: '' };
    const val = cents / 100;
    return {
      valor: val,
      formatted: val.toLocaleString(this.moedaConfig.locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    };
  }

  /** Formata um número para exibição no input (conforme casas/locale da moeda). */
  private formatarValorParaExibicao(valor: number | null | undefined): string {
    if (valor == null || isNaN(valor) || valor < 0) return '';
    const casas = this.casasDecimaisMoeda;
    return valor.toLocaleString(this.moedaConfig.locale, {
      minimumFractionDigits: casas,
      maximumFractionDigits: casas,
    });
  }

  /** Formata o dia enquanto digita (só dígitos, no máximo 2). */
  formatarDia(event: Event): void {
    const input = event.target as HTMLInputElement;
    const onlyDigits = (input.value || '').replace(/\D+/g, '').slice(0, 2);
    this.diaCobrancaEdit = onlyDigits ? parseInt(onlyDigits, 10) : null;
    input.value = onlyDigits;
  }

  /** Confirma o dia de cobrança (Enter ou botão): emite só quando muda e é válido (1-31). */
  confirmarDiaCobranca(): void {
    const atual = this.contrato?.diaCobranca ?? null;
    const novo = this.diaCobrancaEdit;
    if (novo != null && novo >= 1 && novo <= 31 && novo !== atual) {
      this.diaCobrancaChange.emit(novo);
    } else {
      this.reverterDiaCobranca();
    }
  }

  /** Reverte o input de dia ao valor atual do contrato. */
  reverterDiaCobranca(): void {
    this.diaCobrancaEdit = this.contrato?.diaCobranca ?? null;
  }

  /** Formata as parcelas por mês enquanto digita (só dígitos, no máximo 2). */
  formatarParcelasPorMes(event: Event): void {
    const input = event.target as HTMLInputElement;
    const onlyDigits = (input.value || '').replace(/\D+/g, '').slice(0, 2);
    this.parcelasPorMesEdit = onlyDigits ? parseInt(onlyDigits, 10) : null;
    input.value = onlyDigits;
  }

  /** Confirma as parcelas por mês (Enter ou botão): emite só quando muda e é válido (>= 1). */
  confirmarParcelasPorMes(): void {
    const atual = this.contrato?.parcelasPorMes ?? null;
    const novo = this.parcelasPorMesEdit;
    if (novo != null && novo >= 1 && novo !== atual) {
      this.parcelasPorMesChange.emit(novo);
    } else {
      this.reverterParcelasPorMes();
    }
  }

  /** Reverte o input de parcelas por mês ao valor atual do contrato. */
  reverterParcelasPorMes(): void {
    this.parcelasPorMesEdit = this.contrato?.parcelasPorMes ?? null;
  }

  /** So e cancelavel enquanto ATIVO/PENDENTE (espelha a regra do backend). */
  get cancelavel(): boolean {
    const status = this.contrato?.status;
    return !status || status === 'ATIVO' || status === 'PENDENTE';
  }

  /** Rotulo traduzido do mes de referencia atual (usado no modo somente leitura). */
  get mesReferenciaLabelSelecionada(): string {
    const opt = this.mesReferenciaOptions.find((o) => o.value === this.mesReferenciaValor);
    return opt ? opt.label : '-';
  }

  constructor(private translate: TranslateService) {}

  /**
   * True quando a situacao e ATIVO (tag verde) OU quando o status esta ausente
   * (preserva o comportamento historico: os endpoints do card so trazem o
   * contrato vigente, sempre ATIVO). Outros status usam a tag neutra.
   */
  get statusAtivo(): boolean {
    const status = this.contrato?.status;
    return !status || status === 'ATIVO';
  }

  /**
   * Rotulo localizado da situacao (usado na tag neutra dos status nao-ATIVO).
   * Fallback padrao do projeto: se a chave nao existir, exibe o enum cru.
   */
  statusLabel(): string {
    const status = this.contrato?.status;
    if (!status) {
      return this.translate.instant('contador_operar.despesa_acessoria.contrato.status_ativo');
    }
    const chave = `contador_operar.despesa_acessoria.contrato.status_valores.${status.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? status : traduzido;
  }
}
